import NiceModal from '@ebay/nice-modal-react';
import { Alert, Button, Input, message, Modal, Radio, Space, Tag } from 'antd';
import { observer } from 'mobx-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { post } from '../../../../axios/axios';
import { useDevice } from '../../../../hooks/useDevice';
import {
    clearPackageEntitlement,
    getPackageDocType,
    getSelectedPackageEntitlement,
    isAmountEntitlement,
    selectPackageEntitlement,
} from '../../../../Service/packageEntitlements';
import GroupBuyingOrder from '../../../../components/GroupBuyingOrder/GroupBuyingOrder';
import { summaryStore } from '../../store';

const SERVICE_NAMES = {
    openingReport: '开题报告',
    taskAssignment: '任务书',
    defenseSlides: '答辩PPT',
    surveyQuestionnaire: '调查问卷',
    literatureReview: '文献综述',
    aigc: '降AI率',
};
const DOC_NAMES = {
    GRADUATION_THESIS: '长文',
    PROPOSAL_REPORT: '开题报告',
    LITERATURE_REVIEW: '文献综述',
    DEFENSE_PPT: '答辩PPT',
    THESIS_MODIFICATION: '降重',
    FORMATTER: '格式精修',
};
const isAigc = (stype) => stype === 'aigc' || stype?.endsWith('_aigc');
const normalizeDocType = (type) =>
    ['TERM_PAPER', 'COURSE_THESIS'].includes(type) ? 'GRADUATION_THESIS' : type;

const RedeemModal = NiceModal.create(
    observer(({ orderNo, initialCode = '' }) => {
        const modal = NiceModal.useModal();
        const { isMobile } = useDevice();
        const [code, setCode] = useState('');
        const [info, setInfo] = useState(null);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState('');
        const busy = useRef(false);
        const request = useRef(0);
        const submission = useRef(null);
        const isArticle =
            !!orderNo?.startsWith('ML') &&
            !orderNo.startsWith('MLP') &&
            !orderNo.startsWith('MLMI');
        // Repricing changes order numbers; the paper id is the stable owner of an article retry.
        const owner = isArticle
            ? `paper:${summaryStore.paperInfo?.id ?? orderNo}`
            : `order:${orderNo || ''}`;
        const targetDocType = isArticle
            ? normalizeDocType(summaryStore.docType)
            : orderNo?.startsWith('MLP')
              ? 'DEFENSE_PPT'
              : orderNo?.startsWith('R')
                ? 'THESIS_MODIFICATION'
                : orderNo?.startsWith('MLMI')
                  ? 'FORMATTER'
                  : null;

        const incompatibility = useCallback(
            (record) => {
                if (!record) return '';
                if (Number(record.codeType) === 1)
                    return '优惠券请在支付页面确认抵扣，不能用于兑换全文。';
                if (isAmountEntitlement(record)) return '';
                const recordType = normalizeDocType(getPackageDocType(record));
                if (!recordType || (targetDocType && recordType !== targetDocType)) {
                    return `此权益不适用于当前${DOC_NAMES[targetDocType] || '内容'}，请选择对应类型的套餐。`;
                }
                return '';
            },
            [targetDocType],
        );

        const query = useCallback(
            async (value, selectedRecord) => {
                const normalized = value.trim();
                if (
                    submission.current?.owner !== owner ||
                    submission.current?.code !== normalized
                ) {
                    submission.current = null;
                }
                const selected = getSelectedPackageEntitlement();
                const record =
                    selectedRecord || (selected?.redeemCode === normalized ? selected : null);
                const isAmount = isAmountEntitlement(record || { redeemCode: normalized });
                const requestId = ++request.current;
                setCode(normalized);
                setInfo(null);
                setError('');
                if (!normalized) {
                    setError('请输入已有兑换码，或从“我的套餐”中选择。');
                    return;
                }
                const mismatch = incompatibility(record);
                if (mismatch) {
                    setError(mismatch);
                    setLoading(false);
                    return;
                }
                setLoading(true);
                try {
                    let services = {};
                    if (!isAmount && normalized.startsWith('MLMP')) {
                        const response = await post('/member/redeem/code/detail', {
                            code: normalized,
                        });
                        if (!response.success)
                            throw new Error(response.msg || '查询套餐失败，请重试');
                        services = response.data || {};
                    }
                    if (request.current === requestId)
                        setInfo({ code: normalized, services, record, owner, isAmount });
                } catch (err) {
                    if (request.current === requestId) setError(err.message || '查询失败，请重试');
                } finally {
                    if (request.current === requestId) setLoading(false);
                }
            },
            [owner, incompatibility],
        );

        useEffect(() => {
            if (submission.current?.owner !== owner) submission.current = null;
            if (modal.visible) {
                const selected = getSelectedPackageEntitlement();
                const value = initialCode || selected?.redeemCode || '';
                setCode(value);
                setInfo(null);
                setError('');
                setLoading(false);
                busy.current = false;
                if (value) query(value);
            }
            return () => {
                request.current++;
            };
            // owner deliberately stays stable when the same paper receives a replacement order number.
        }, [modal.visible, initialCode, owner, query]);

        const usePackage = async () => {
            if (busy.current || !info || summaryStore.isExtraloading) return;
            if (info.owner !== owner) {
                setError('当前内容已变化，请重新选择套餐。');
                return;
            }
            const mismatch = incompatibility(info.record);
            if (mismatch) {
                setError(mismatch);
                return;
            }
            if (submission.current?.owner !== owner || submission.current?.code !== info.code) {
                submission.current = null;
            }
            const requestId = ++request.current;
            busy.current = true;
            setLoading(true);
            setError('');
            try {
                let currentOrderNo = submission.current?.orderNo || orderNo;
                if (isArticle && !submission.current) {
                    if (!summaryStore.paperInfo?.orderNo) throw new Error('请先完成题目和大纲');
                    if (!info.isAmount && info.code.startsWith('MLMP')) {
                        const stypes = Object.keys(info.services).filter(
                            (key) => info.services[key] === true && key !== 'aigc',
                        );
                        if (info.services.aigc) {
                            const aigc =
                                summaryStore.serviceList.find(
                                    (service) => isAigc(service.stype) && service.checked,
                                ) ||
                                summaryStore.serviceList.find((service) => isAigc(service.stype));
                            if (aigc) stypes.push(aigc.stype);
                        }
                        summaryStore.setServiceListByStype(stypes);
                    }
                    const quote = await summaryStore.getPriceAfterAddServices();
                    if (request.current !== requestId) return;
                    if (!quote?.orderNo) throw new Error('套餐服务确认失败，请重试');
                    currentOrderNo = quote.orderNo;
                }
                if (!currentOrderNo) throw new Error('未找到需要使用套餐的订单');
                // Retrying a timeout must reuse both the same code and the same owner's confirmed order.
                submission.current = { owner, code: info.code, orderNo: currentOrderNo };
                const result = await post('/member/payment/redeemCodeNew', {
                    orderNo: currentOrderNo,
                    code: info.code,
                });
                if (request.current !== requestId) return;
                if (!result.success || result.data === false)
                    throw new Error(result.msg || '套餐使用未完成，请重试');
                if (getSelectedPackageEntitlement()?.redeemCode === info.code)
                    clearPackageEntitlement();
                submission.current = null;
                message.success('兑换码已用于当前内容，可在订单中查看生成结果');
                modal.resolve({
                    code: info.code,
                    orderNo: currentOrderNo,
                    services: Object.keys(info.services)
                        .filter((key) => info.services[key])
                        .map((stype) => ({ stype })),
                });
                modal.hide();
            } catch (err) {
                if (request.current === requestId)
                    setError(err.response?.data?.msg || err.message || '使用失败，请重试');
            } finally {
                if (request.current === requestId) {
                    busy.current = false;
                    setLoading(false);
                }
            }
        };

        const choosePackage = () =>
            NiceModal.show(GroupBuyingOrder, {
                onSelect: (record) => {
                    const mismatch = incompatibility(record);
                    if (mismatch) throw new Error(mismatch);
                    selectPackageEntitlement(record);
                    query(record.redeemCode, record);
                },
            }).catch(() => {});

        return (
            <Modal
                title="使用已购套餐"
                open={modal.visible}
                width={isMobile ? 'calc(100% - 24px)' : 580}
                footer={null}
                maskClosable={false}
                closable={!loading}
                zIndex={1890}
                onCancel={() => {
                    modal.hide();
                    modal.reject();
                }}
            >
                <Alert
                    type="info"
                    showIcon
                    message="确认生成时才使用套餐"
                    description="选择已有套餐，或输入以前购买的兑换码。同一订单使用未完成时，可以重试。"
                />
                <Space.Compact style={{ width: '100%', margin: '20px 0 12px' }}>
                    <Input
                        value={code}
                        disabled={loading}
                        placeholder="输入已有兑换码"
                        onChange={(event) => {
                            setCode(event.target.value);
                            setInfo(null);
                            setError('');
                        }}
                        onPressEnter={() => query(code)}
                    />
                    <Button loading={loading} onClick={() => query(code)}>
                        查看套餐
                    </Button>
                </Space.Compact>
                <Button type="link" disabled={loading} onClick={choosePackage}>
                    从我的套餐中选择
                </Button>
                {error && (
                    <Alert type="error" showIcon message={error} style={{ margin: '12px 0' }} />
                )}
                {info && (
                    <div
                        style={{
                            marginTop: 16,
                            padding: 20,
                            border: '1px solid #e9e4f0',
                            borderRadius: 12,
                        }}
                    >
                        <h3 style={{ margin: '0 0 12px' }}>
                            {info.isAmount
                                ? '普通金额兑换码'
                                : info.code.startsWith('MLMP')
                                  ? '套餐包含的服务'
                                  : '使用此凭证解锁当前内容'}
                        </h3>
                        {isArticle && !info.isAmount && info.code.startsWith('MLMP') && (
                            <Tag color="purple">文章全文</Tag>
                        )}
                        {Object.entries(SERVICE_NAMES)
                            .filter(([key]) => info.services[key])
                            .map(([key, name]) => (
                                <Tag key={key} style={{ marginBottom: 8 }}>
                                    {name}
                                </Tag>
                            ))}
                        {isArticle && info.services.aigc && (
                            <Radio.Group
                                disabled={loading || !!submission.current}
                                style={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 12,
                                    marginTop: 12,
                                }}
                                value={
                                    summaryStore.serviceList.find(
                                        (service) => isAigc(service.stype) && service.checked,
                                    )?.stype ||
                                    summaryStore.serviceList.find((service) =>
                                        isAigc(service.stype),
                                    )?.stype
                                }
                                onChange={(event) =>
                                    summaryStore.onServiceCheck(true, event.target.value)
                                }
                            >
                                {summaryStore.serviceList
                                    .filter((service) => isAigc(service.stype))
                                    .map((service) => (
                                        <Radio key={service.id} value={service.stype}>
                                            {service.sname}
                                        </Radio>
                                    ))}
                            </Radio.Group>
                        )}
                        <p style={{ color: '#647087', marginTop: 16 }}>
                            {info.isAmount
                                ? '保留当前已选服务，按当前订单金额兑换。确认时会核验兑换码额度是否足够；此码为一次使用，不保留余额。'
                                : '确认后将关联到当前订单，无需再次支付。套餐以外的服务不会自动加入。'}
                        </p>
                        <Button
                            type="primary"
                            size="large"
                            block
                            loading={loading}
                            disabled={summaryStore.isExtraloading}
                            onClick={usePackage}
                        >
                            确认使用套餐并生成
                        </Button>
                    </div>
                )}
            </Modal>
        );
    }),
);

export default RedeemModal;
